/* global ownerDocument */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import set from 'lodash/set';
import intersection from 'lodash/intersection';
import { FLOW_TYPE } from './AppConstants';

/**
 * Flatten an object.
 * From: https://github.com/yahoo/react-intl/wiki/Upgrade-Guide#flatten-messages-object
 *
 * @param object - Object to flatten
 * @param prefix - Prefix to prepend to all keys
 * @param excludedKeys - Do not flatten past this level if any of these keys are detected
 * @returns {{}}
 */
export const flattenObject = (object, prefix = '', excludedKeys = []) => {
    return Object.keys(object).reduce((acc, key) => {
        const value = object[key];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value !== 'object') {
            acc[prefixedKey] = value;
        } else if (intersection(excludedKeys, Object.keys(value)).length === 0) {
            Object.assign(acc, flattenObject(value, prefixedKey, excludedKeys));
        } else {
            acc[prefixedKey] = value;
        }
        return acc;
    }, {});
};

export const expandObject = object => {
    return Object.keys(object).reduce((acc, currField) => {
        set(acc, currField, object[currField]);
        return acc;
    }, {});
};

export const getI18n = (localePrefix) => (id, ...ext) => // eslint-disable-line react/display-name
    (<FormattedMessage id={`${localePrefix}.${id}`} {...ext} />);

export const httpFormSubmit = (method, path, params) => {
    method = method || 'post'; // Set method to post by default if not specified.

    // The rest of this code assumes you are not using a library.
    // It can be made less wordy if you use one.
    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            const hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', key);
            hiddenField.setAttribute('value', params[key]);

            form.appendChild(hiddenField);
        }
    }

    document.body.appendChild(form);
    form.submit();
};

export const isDevEnv = state => get(state, 'serverData.env') === 'development';

/*
 * Slight modification to the polyfill from here:
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/matches#Polyfill
 *
 * we are passing in the element to match, and adding try/catch
 * (in case querySelectorAll() doesn't work)
 */
export const matchesSelector = (elem, selector) => {
    try {
        const matchesFn = elem.matches ||
            elem.matchesSelector ||
            elem.mozMatchesSelector ||
            elem.msMatchesSelector ||
            elem.oMatchesSelector ||
            elem.webkitMatchesSelector;
        if (typeof matchesFn === 'function') {
            return matchesFn.call(elem, selector);
        } else {
            const matches = (document || ownerDocument)
                .querySelectorAll(selector);
            let i = matches.length;
            while (--i >= 0 && matches.item(i) !== elem) {
                // do nothing
            }
            return i > -1;
        }
    } catch (err) {
        // do nothing
    }
    return false;
};

/**
 * Ensure form option is a valid option, otherwise provide a default value.
 *
 * @param {String} valueToCheck
 * @param {Array} validOptions
 * @param {String} defaultValue
 * @returns {String}
 */
export const sanitizeFormInputOptions = (valueToCheck, validOptions, defaultValue) => {
    if (!Array.isArray(validOptions) || validOptions.indexOf(valueToCheck) === -1) {
        return defaultValue;
    }

    return valueToCheck;
};

export const isXOFlow = (flow) => (flow === FLOW_TYPE.HERMES || flow === FLOW_TYPE.PROX);

/**
 * Add a query param to a URL
 *
 * @param {String} url
 * @param {Object} params Object of key/value pairs to append to URL
 * @returns {String}
 */
export const appendUrlParam = (url, params) => {
    if (typeof url !== 'string') {
        throw new TypeError(`${url} is not a valid string`);
    } else if (typeof params !== 'object' || Array.isArray(params)) {
        throw new TypeError(`${params} is not a valid object`);
    }

    const queryParams = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');

    if (!queryParams) {
        return url;
    }

    const questionMarkIndex = url.indexOf('?');

    if (questionMarkIndex === -1) {
        url += '?';
    } else if (questionMarkIndex < url.length - 1) {
        url += '&';
    }

    url += queryParams;

    return url;
};

export const popupWithHTML = ({
    title = 'PayPal Credit',
    params = 'width=835, height=500, location=1, status=1, toolbar=0, menubar=0, resizable=1, scrollbars=1',
    html = ''
}) => {
    const popupWindow = window.open('about:blank', title, params);
    popupWindow.focus();
    popupWindow.document.write(`<!DOCTYPE html>${html}`);
    return popupWindow;
};

export const getViewName = () => {
    const pathName = window.location.pathname || '';
    return pathName.substr(pathName.lastIndexOf('/') + 1);
};

// https://stackoverflow.com/a/326076
export const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

export const uniqueTimestampId = () => {
    try {
        return (
            Math.random()
                .toString(16)
                .slice(2) +
            '-' +
            Date.now()
        );
    } catch (_e) {
        return 'empty-id';
    }
};
